import { useReducer, useEffect } from "react";
import { DbUser } from "../../../../interfaces";
import { Courses } from "@backend-types";
import { EditCourseProps } from "../EditDetails/EditCourse";

export type CourseEditState = {
  form: {
    deepLinkAssetType?: string;
    gradingMethod?: string | null;
    scoreType?: string;
    isModuleLocked?: boolean;
    showModuleOrder?: boolean;
    assessmentMetricsType?: string | null;
    showSubmoduleProgressBar?: boolean;
  };
  data: {
    courses: {
      value: Courses[];
      succeeded: boolean;
    };
  };
  currentUser: DbUser;
  course?: Courses;
};

export type UpdateFormAction = {
  type: "updateForm";
  update: Partial<CourseEditState["form"]>;
};
export type ReplaceStateAction = {
  type: "replaceState";
  state: CourseEditState;
};

export type DataSuccessAction<T extends keyof CourseEditState["data"]> = {
  type: "dataSuccess";
  key: T;
  value: CourseEditState["data"][T]["value"];
};

export type Action =
  | UpdateFormAction
  | ReplaceStateAction
  | DataSuccessAction<keyof CourseEditState["data"]>;

export const dataSuccess = <T extends keyof CourseEditState["data"]>({
  key,
  value,
}: Omit<DataSuccessAction<T>, "type">): DataSuccessAction<T> => ({
  type: "dataSuccess",
  key,
  value,
});

const normalizeGradingFromMetadata = (value?: string) => ({
  gradingMethod: value === "completion" ? "completion" : "accuracy",
  scoreType: value === "completion" ? "" : value ?? "",
});

export const initCourseEditState = (
  currentUser: DbUser,
  data?: CourseEditState["data"],
  course?: Courses
): CourseEditState => {
  const grading = normalizeGradingFromMetadata(course?.metadata?.lti_grading_method);

  const state: CourseEditState = {
    form: {
      deepLinkAssetType: course?.metadata?.deeplink_asset_type,
      gradingMethod: grading.gradingMethod,
      scoreType: grading.scoreType,
      isModuleLocked: course?.metadata.use_digit_study_tool,
      showModuleOrder: course?.metadata?.show_module_order,
      assessmentMetricsType: course?.metadata?.management_page?.assessment_metrics_type,
      showSubmoduleProgressBar:
        course?.metadata?.management_page?.show_submodule_progress,
    },
    data: data ?? {
      courses: {
        value: [],
        succeeded: false,
      },
    },
    currentUser,
    course: course,
  };

  return state;
};

function reducer(state: CourseEditState, action: Action): CourseEditState {
  switch (action.type) {
    case "replaceState":
      return action.state;
    case "updateForm": {
      return {
        ...state,
        form: { ...state.form, ...action.update },
      };
    }
    case "dataSuccess": {
      return {
        ...state,
        data: {
          ...state.data,
          [action.key]: {
            value: action.value,
            succeeded: true,
          },
        },
      };
    }
  }
}

export function useCourseEditState(props: EditCourseProps) {
  const [state, dispatch] = useReducer(
    reducer,
    initCourseEditState(props.currentUser, undefined, props.course as Courses)
  );

  useEffect(() => {
    if (props.open && props.course) {
      dispatch({
        type: "replaceState",
        state: initCourseEditState(props.currentUser, undefined, props.course as Courses),
      });
    }
  }, [props.open, props.course, props.currentUser]);

  return [state, dispatch] as const;
}
